import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PageTitle } from '@/components/PageTitle';
import { StatCard } from '@/components/StatCard';
import { ConfirmDialog } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDateTime, formatNumber } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { Link } from '@/lib/router-compat';
import { useCan } from '@/lib/session';
import { CrmStatusBadge } from '@/components/CrmStatusBadge';

const statuses = ['pending', 'sending', 'sent', 'failed', 'held', 'skipped', 'simulated'] as const;
type Status = (typeof statuses)[number];

export const Route = createFileRoute('/_app/crm/')({
  validateSearch: z.object({ status: z.enum(statuses).optional() }),
  beforeLoad: ({ context }) => requirePermission(context.me, 'crm.view'),
  component: CrmPage
});

type Item = Awaited<ReturnType<typeof client.crm.queue>>['items'][number];

function CrmPage() {
  const { t } = useTranslation();
  const can = useCan();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<'enable' | 'releaseAll' | null>(null);

  const st = useQuery({ ...orpc.crm.status.queryOptions(), refetchInterval: 10_000 });
  const queue = useInfiniteQuery({
    ...orpc.crm.queue.infiniteOptions({
      input: (offset: number) => ({ limit: 50, offset, ...(status && { status: [status] }) }),
      initialPageParam: 0,
      getNextPageParam: (last, pages) => (last.hasMore ? pages.length * 50 : undefined)
    }),
    enabled: st.data?.configured === true,
    refetchInterval: 10_000
  });
  const items = queue.data?.pages.flatMap((p) => p.items) ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.crm.key() });
  const onError = (e: unknown) => toast('error', errorText(t, e));
  const toggle = useMutation({
    mutationFn: (syncEnabled: boolean) => client.crm.updateSettings({ syncEnabled }),
    onSuccess: async () => {
      setConfirm(null);
      await refresh();
    },
    onError
  });
  const release = useMutation({
    mutationFn: (ids?: string[]) => client.crm.release(ids ? { ids } : {}),
    onSuccess: async (r) => {
      setConfirm(null);
      setSelected(new Set());
      toast('success', t('crm.released', { count: r.released }));
      await refresh();
    },
    onError
  });
  const retry = useMutation({
    mutationFn: (id: string) => client.crm.retry({ id }),
    onSuccess: async () => {
      toast('success', t('crm.retried'));
      await refresh();
    },
    onError
  });

  const s = st.data;
  const crumbs = [{ label: t('crm.section') }, { label: t('crm.queue'), active: true }];
  if (s && !s.configured) {
    return (
      <div className="space-y-6 p-6">
        <PageTitle title={t('crm.title')} items={crumbs} />
        <div className="alert alert-info alert-soft text-sm">{t('crm.notConfigured')}</div>
      </div>
    );
  }

  const sent = s ? (s.counts.sent ?? 0) + (s.counts.sending ?? 0) : 0;
  const heldTotal = s?.counts.held ?? 0;
  const canRelease = can('crm.sync.release');
  const canManage = can('crm.mapping.manage');

  return (
    <div className="space-y-6 p-6">
      <PageTitle title={t('crm.title')} items={crumbs} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon="lucide--database"
          title={t('crm.instance')}
          footer={
            s && (
              <p className={`flex items-center gap-1.5 font-medium ${s.dryRun ? 'text-info' : 'text-success'}`}>
                <span className={`iconify size-3.5 ${s.dryRun ? 'lucide--flask-conical' : 'lucide--radio'}`} />
                {s.dryRun ? t('crm.modeDryRun') : t('crm.modeLive')}
              </p>
            )
          }
        >
          <p className="truncate font-mono text-sm" title={s?.instanceUri ?? ''}>
            {s?.instanceUri?.replace(/^https:\/\//, '') ?? '…'}
          </p>
        </StatCard>
        <StatCard
          icon={s?.syncEnabled ? 'lucide--play' : 'lucide--pause'}
          title={t('crm.mode')}
          value={s ? (s.syncEnabled ? t('crm.on') : t('crm.off')) : '…'}
          footer={
            s && (
              <>
                <p className="text-base-content/70">{s.syncEnabled ? null : t('crm.syncOffHint')}</p>
                {canManage && (
                  <button
                    type="button"
                    className={`btn btn-xs ${s.syncEnabled ? 'btn-outline border-base-300' : 'btn-primary'}`}
                    disabled={toggle.isPending}
                    onClick={() => (s.syncEnabled ? toggle.mutate(false) : setConfirm('enable'))}
                  >
                    {s.syncEnabled ? t('crm.disable') : t('crm.enable')}
                  </button>
                )}
              </>
            )
          }
        />
        <StatCard
          icon="lucide--gauge"
          title={t('crm.cap')}
          value={s ? (s.cap === null ? t('crm.capUnlimited', { sent: formatNumber(sent) }) : t('crm.capUsage', { sent: formatNumber(sent), cap: formatNumber(s.cap) })) : '…'}
        >
          {s && s.cap !== null && <progress className="progress progress-primary" value={Math.min(sent, s.cap)} max={Math.max(s.cap, 1)} />}
        </StatCard>
        <StatCard
          icon="lucide--hand"
          title={t('crm.held')}
          value={formatNumber(heldTotal)}
          footer={
            s &&
            heldTotal > 0 && (
              <>
                <ul className="text-base-content/70">
                  {Object.entries(s.heldByReason).map(([reason, n]) => (
                    <li key={reason}>
                      {t(`crm.holdReasons.${reason as keyof typeof s.heldByReason}`)}: {formatNumber(n)}
                    </li>
                  ))}
                </ul>
                {canRelease && (
                  <button type="button" className="btn btn-xs btn-outline border-base-300" onClick={() => setConfirm('releaseAll')}>
                    {t('crm.releaseAll', { count: heldTotal })}
                  </button>
                )}
              </>
            )
          }
        />
      </div>

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="tablist" className="tabs tabs-box tabs-sm">
              {([undefined, ...statuses] as (Status | undefined)[]).map((key) => (
                <button
                  key={key ?? 'all'}
                  type="button"
                  role="tab"
                  className={`tab ${status === key ? 'tab-active' : ''}`}
                  onClick={() => {
                    setSelected(new Set());
                    void navigate({ search: key ? { status: key } : {}, replace: true });
                  }}
                >
                  {key ? t(`crm.statuses.${key}`) : t('crm.filterAll')}
                  {key && s?.counts[key] ? <span className="text-base-content/50 ms-1">{s.counts[key]}</span> : null}
                </button>
              ))}
            </div>
            {canRelease && selected.size > 0 && (
              <button type="button" className="btn btn-sm btn-primary" disabled={release.isPending} onClick={() => release.mutate([...selected])}>
                {t('crm.releaseSelected', { count: selected.size })}
              </button>
            )}
          </div>

          <div className="mt-2 overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  {canRelease && <th className="w-8" />}
                  <th>{t('crm.lead')}</th>
                  <th>{t('leads.form')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('crm.createdAt')}</th>
                  <th>{t('crm.sentAt')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <QueueRow
                    key={i.id}
                    item={i}
                    selectable={canRelease}
                    selected={selected.has(i.id)}
                    onSelect={(on) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(i.id);
                        else next.delete(i.id);
                        return next;
                      })
                    }
                    onRetry={can('crm.sync.retry') ? () => retry.mutate(i.id) : undefined}
                  />
                ))}
              </tbody>
            </table>
            {queue.isSuccess && items.length === 0 && <p className="text-base-content/60 p-4 text-sm">{t('crm.noRows')}</p>}
          </div>
          {queue.hasNextPage && (
            <div>
              <button type="button" className="btn btn-sm btn-ghost" disabled={queue.isFetchingNextPage} onClick={() => void queue.fetchNextPage()}>
                {t('admin.loadMore')}
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirm === 'enable'}
        busy={toggle.isPending}
        text={s && (s.cap === null ? t('crm.confirmEnableUnlimited') : t('crm.confirmEnable', { cap: s.cap }))}
        onClose={() => setConfirm(null)}
        onConfirm={() => toggle.mutate(true)}
      />
      <ConfirmDialog
        open={confirm === 'releaseAll'}
        busy={release.isPending}
        text={t('crm.confirmReleaseAll', { count: heldTotal })}
        onClose={() => setConfirm(null)}
        onConfirm={() => release.mutate(undefined)}
      />
    </div>
  );
}

function QueueRow({
  item: i,
  selectable,
  selected,
  onSelect,
  onRetry
}: {
  item: Item;
  selectable: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  onRetry: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  return (
    <tr className="align-top">
      {selectable && (
        <td>
          {i.status === 'held' && (
            <input type="checkbox" className="checkbox checkbox-sm" aria-label={i.name ?? i.leadId} checked={selected} onChange={(e) => onSelect(e.target.checked)} />
          )}
        </td>
      )}
      <td>
        <Link to={`/leads/${encodeURIComponent(i.leadId)}`} className="link link-hover font-medium">
          {i.name ?? '–'}
        </Link>
        <p className="text-base-content/60 text-xs">{[i.email, i.company].filter(Boolean).join(' · ')}</p>
      </td>
      <td className="text-sm">
        {i.formName ?? '–'}
        {i.campaignName && <p className="text-base-content/60 text-xs">{i.campaignName}</p>}
      </td>
      <td>
        <CrmStatusBadge item={i} />
        {i.matchResult && i.matchResult !== 'none' && <p className="text-base-content/60 mt-1 text-xs">{t(`crm.match.${i.matchResult}` as never, { defaultValue: i.matchResult })}</p>}
        {i.lastError && i.status === 'failed' && (
          <p className="text-error mt-1 max-w-md text-xs break-words">
            {i.lastError}
            {i.requestId && <span className="text-base-content/50 block font-mono">{t('crm.requestId')}: {i.requestId}</span>}
          </p>
        )}
      </td>
      <td className="text-sm whitespace-nowrap">
        {formatDateTime(i.createdAt)}
        {i.attempts > 1 && <p className="text-base-content/60 text-xs">{t('crm.attempts')}: {i.attempts}</p>}
      </td>
      <td className="text-sm whitespace-nowrap">{formatDateTime(i.sentAt)}</td>
      <td className="text-end whitespace-nowrap">
        {i.crmUrl && (
          <a href={i.crmUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-xs">
            <span className="iconify lucide--external-link size-3.5" />
            {t('crm.openInCrm')}
          </a>
        )}
        {i.status === 'failed' && onRetry && (
          <button type="button" className="btn btn-ghost btn-xs" onClick={onRetry}>
            <span className="iconify lucide--rotate-ccw size-3.5" />
            {t('crm.retry')}
          </button>
        )}
      </td>
    </tr>
  );
}
